import { useState, useEffect } from 'react';
import { 
  Check, 
  X, 
  Eye, 
  ChevronLeft,
  ChevronRight,
  FileCheck,
  AlertTriangle,
  Globe,
  User,
  Mail,
  Phone,
  MapPin,
  Shield,
  Wallet,
  Image,
  FileText,
  ArrowLeft,
  Edit,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { kycAPI, countryAPI, complianceAPI } from '../lib/api';
import { formatDateTime, getStatusColor, formatCurrency } from '../lib/utils';
import { toast } from 'sonner';

const StatusBadge = ({ status }) => {
  const color = getStatusColor(status);
  const colorClasses = {
    success: 'badge-success',
    warning: 'badge-warning',
    danger: 'badge-danger',
    info: 'badge-info',
    neutral: 'badge-neutral',
  };
  
  return (
    <span className={`badge ${colorClasses[color]}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

const DocumentTypeBadge = ({ type }) => {
  const labels = {
    aadhaar: { label: 'Aadhaar', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    pan: { label: 'PAN Card', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    passport: { label: 'Passport', color: 'bg-violet-500/20 text-violet-400 border-violet-500/30' },
    driving_license: { label: 'Driving License', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  };
  
  const config = labels[type] || { label: type, color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' };
  
  return (
    <span className={`badge border ${config.color}`}>
      {config.label}
    </span>
  );
};

// Document Status Icon
const DocStatusIcon = ({ status }) => {
  if (status === 'approved') return <CheckCircle className="w-4 h-4 text-emerald-400" />;
  if (status === 'rejected') return <XCircle className="w-4 h-4 text-red-400" />;
  if (status === 'pending') return <AlertCircle className="w-4 h-4 text-amber-400" />;
  return <AlertCircle className="w-4 h-4 text-muted-foreground" />;
};

// KYC Detail View Component
const KYCDetailView = ({ docId, onBack, onActionComplete }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [editData, setEditData] = useState({
    document_number: '',
    status: '',
    kyc_level: '',
    notes: ''
  });
  const [actionLoading, setActionLoading] = useState(false);

  const fetchDocument = async () => {
    try {
      setLoading(true);
      const response = await kycAPI.getById(docId);
      setDoc(response.data);
      setEditData({
        document_number: response.data.document_number || '',
        status: response.data.status || 'pending',
        kyc_level: response.data.kyc_level || 'level_1',
        notes: ''
      });
    } catch (error) {
      toast.error('Failed to fetch document details');
      onBack();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocument();
  }, [docId]);

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      await kycAPI.approve(docId);
      toast.success('KYC approved successfully');
      onActionComplete();
    } catch (error) {
      toast.error('Failed to approve KYC');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      setActionLoading(true);
      await kycAPI.reject(docId, rejectReason);
      toast.success('KYC rejected');
      setRejectDialog(false);
      onActionComplete();
    } catch (error) {
      toast.error('Failed to reject KYC');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = async () => {
    try {
      setActionLoading(true);
      await kycAPI.update(docId, editData);
      toast.success('KYC document updated');
      setEditDialog(false);
      fetchDocument();
    } catch (error) {
      toast.error('Failed to update KYC');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Document not found</p>
        <Button variant="outline" onClick={onBack} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to List
        </Button>
      </div>
    );
  }

  const isIndianUser = doc.user_country === 'IN';
  const isPending = doc.status === 'pending';
  const isResubmission = doc.status === 'resubmission_required';
  const canTakeAction = isPending || isResubmission;

  return (
    <div className="space-y-6" data-testid="kyc-detail-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={onBack} data-testid="back-to-list-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h2 className="text-xl font-semibold">KYC Document Review</h2>
            <p className="text-sm text-muted-foreground">Review and verify user documents</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={doc.status} />
          {isIndianUser && (
            <span className="badge bg-orange-500/20 text-orange-400 border border-orange-500/30">
              India - PAN + Aadhaar Required
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - User Info */}
        <div className="space-y-6">
          {/* User Details Card */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <User className="w-4 h-4 text-primary" />
              User Information
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center text-primary font-bold text-lg">
                  {doc.user_name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="font-medium">{doc.user_name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{doc.user_id?.slice(0, 16)}...</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>{doc.user_email || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span>{doc.user_mobile || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">{doc.user_country === 'IN' ? 'India' : doc.user_country || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Wallet Info Card */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Wallet className="w-4 h-4 text-primary" />
              Financial Summary
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Wallet Balance</span>
                <span className="font-mono font-medium text-emerald-400">
                  {formatCurrency(doc.user_wallet_balance || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Total Deposits</span>
                <span className="font-mono text-sm">{formatCurrency(doc.user_total_deposits || 0)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Total Withdrawals</span>
                <span className="font-mono text-sm">{formatCurrency(doc.user_total_withdrawals || 0)}</span>
              </div>
            </div>
          </div>

          {/* Verification Info */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              Verification Status
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">KYC Level</span>
                <span className="badge badge-info">{doc.kyc_level?.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Submitted</span>
                <span className="text-sm">{formatDateTime(doc.created_at)}</span>
              </div>
              {doc.verified_at && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Verified At</span>
                    <span className="text-sm">{formatDateTime(doc.verified_at)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Verified By</span>
                    <span className="text-sm font-medium">{doc.verified_by_name || 'Admin'}</span>
                  </div>
                </>
              )}
              {doc.rejection_reason && (
                <div className="pt-2 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-1">Rejection Reason</p>
                  <p className="text-sm text-red-400">{doc.rejection_reason}</p>
                </div>
              )}
            </div>
          </div>

          {/* Admin Actions */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              Admin Actions
            </h3>
            <div className="flex flex-col gap-3">
              {canTakeAction && (
                <Button 
                  onClick={handleApprove}
                  disabled={actionLoading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="approve-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Approve KYC
                </Button>
              )}
              {canTakeAction && (
                <Button 
                  variant="destructive"
                  onClick={() => setRejectDialog(true)}
                  disabled={actionLoading}
                  className="w-full"
                  data-testid="reject-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              )}
              <Button 
                variant="outline"
                onClick={() => setEditDialog(true)}
                disabled={actionLoading}
                className="w-full"
                data-testid="edit-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </div>
          </div>
        </div>

        {/* Middle & Right Column - Documents */}
        <div className="lg:col-span-2 space-y-6">
          {/* For Indian Users - Show Both Documents */}
          {isIndianUser ? (
            <>
              {/* Aadhaar Document */}
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary" />
                    Aadhaar Card
                    <span className="text-xs text-red-400 font-normal">(Mandatory)</span>
                  </h3>
                  <div className="flex items-center gap-2">
                    <DocStatusIcon status={doc.aadhaar_status || doc.status} />
                    <StatusBadge status={doc.aadhaar_status || doc.status} />
                  </div>
                </div>
                {doc.aadhaar_number && (
                  <p className="text-sm text-muted-foreground mb-3">
                    Number: <span className="font-mono">{doc.aadhaar_number}</span>
                  </p>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                      <Image className="w-3 h-3" /> Front Side
                    </p>
                    {doc.aadhaar_front_url ? (
                      <div className="relative group">
                        <img 
                          src={doc.aadhaar_front_url} 
                          alt="Aadhaar Front" 
                          className="w-full h-40 object-cover rounded-lg border border-border"
                        />
                        <a 
                          href={doc.aadhaar_front_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                        >
                          <Eye className="w-6 h-6 text-white" />
                        </a>
                      </div>
                    ) : (
                      <div className="w-full h-40 bg-muted rounded-lg flex items-center justify-center text-muted-foreground border border-dashed border-red-500/50">
                        <span className="text-sm text-red-400">Not Uploaded</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                      <Image className="w-3 h-3" /> Back Side
                    </p>
                    {doc.aadhaar_back_url ? (
                      <div className="relative group">
                        <img 
                          src={doc.aadhaar_back_url} 
                          alt="Aadhaar Back" 
                          className="w-full h-40 object-cover rounded-lg border border-border"
                        />
                        <a 
                          href={doc.aadhaar_back_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                        >
                          <Eye className="w-6 h-6 text-white" />
                        </a>
                      </div>
                    ) : (
                      <div className="w-full h-40 bg-muted rounded-lg flex items-center justify-center text-muted-foreground border border-dashed border-red-500/50">
                        <span className="text-sm text-red-400">Not Uploaded</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* PAN Document */}
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary" />
                    PAN Card
                    <span className="text-xs text-red-400 font-normal">(Mandatory)</span>
                  </h3>
                  <div className="flex items-center gap-2">
                    <DocStatusIcon status={doc.pan_status || doc.status} />
                    <StatusBadge status={doc.pan_status || doc.status} />
                  </div>
                </div>
                {doc.pan_number && (
                  <p className="text-sm text-muted-foreground mb-3">
                    Number: <span className="font-mono">{doc.pan_number}</span>
                  </p>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                      <Image className="w-3 h-3" /> Front Side
                    </p>
                    {doc.pan_front_url ? (
                      <div className="relative group">
                        <img 
                          src={doc.pan_front_url} 
                          alt="PAN Front" 
                          className="w-full h-40 object-cover rounded-lg border border-border"
                        />
                        <a 
                          href={doc.pan_front_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                        >
                          <Eye className="w-6 h-6 text-white" />
                        </a>
                      </div>
                    ) : (
                      <div className="w-full h-40 bg-muted rounded-lg flex items-center justify-center text-muted-foreground border border-dashed border-red-500/50">
                        <span className="text-sm text-red-400">Not Uploaded</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-center">
                    <div className="text-center text-muted-foreground">
                      <p className="text-sm">Back side not required</p>
                      <p className="text-xs">for PAN Card</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* For Global Users - Single Document */
            <div className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" />
                  Document Details
                </h3>
                <DocumentTypeBadge type={doc.document_type} />
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Document Number: <span className="font-mono font-medium">{doc.document_number}</span>
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                    <Image className="w-3 h-3" /> Front Side
                  </p>
                  {doc.document_front_url ? (
                    <div className="relative group">
                      <img 
                        src={doc.document_front_url} 
                        alt="Document Front" 
                        className="w-full h-48 object-cover rounded-lg border border-border"
                      />
                      <a 
                        href={doc.document_front_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                      >
                        <Eye className="w-6 h-6 text-white" />
                      </a>
                    </div>
                  ) : (
                    <div className="w-full h-48 bg-muted rounded-lg flex items-center justify-center text-muted-foreground">
                      <FileText className="w-8 h-8" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                    <Image className="w-3 h-3" /> Back Side
                  </p>
                  {doc.document_back_url ? (
                    <div className="relative group">
                      <img 
                        src={doc.document_back_url} 
                        alt="Document Back" 
                        className="w-full h-48 object-cover rounded-lg border border-border"
                      />
                      <a 
                        href={doc.document_back_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                      >
                        <Eye className="w-6 h-6 text-white" />
                      </a>
                    </div>
                  ) : (
                    <div className="w-full h-48 bg-muted rounded-lg flex items-center justify-center text-muted-foreground">
                      <span className="text-sm">Not Required</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Selfie/Photo ID */}
          {doc.selfie_url && (
            <div className="bg-card border border-border rounded-xl p-5">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                Selfie / Photo ID
              </h4>
              <div className="flex gap-4">
                <div className="relative group">
                  <img 
                    src={doc.selfie_url} 
                    alt="User Selfie" 
                    className="w-32 h-32 object-cover rounded-lg border border-border"
                    data-testid="selfie-image"
                  />
                  <a 
                    href={doc.selfie_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                  >
                    <Eye className="w-5 h-5 text-white" />
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject KYC Document</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this document. The user will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Enter rejection reason..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={!rejectReason.trim() || actionLoading}
              data-testid="confirm-reject-btn"
            >
              Reject Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit KYC Document</DialogTitle>
            <DialogDescription>
              Update the document details and status.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Document Number</label>
              <Input
                value={editData.document_number}
                onChange={(e) => setEditData({...editData, document_number: e.target.value})}
                placeholder="Document number"
                data-testid="edit-doc-number"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Status</label>
              <Select 
                value={editData.status} 
                onValueChange={(val) => setEditData({...editData, status: val})}
              >
                <SelectTrigger data-testid="edit-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="resubmission_required">Resubmission Required</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">KYC Level</label>
              <Select 
                value={editData.kyc_level} 
                onValueChange={(val) => setEditData({...editData, kyc_level: val})}
              >
                <SelectTrigger data-testid="edit-level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="level_0">Level 0 - No KYC</SelectItem>
                  <SelectItem value="level_1">Level 1 - Basic</SelectItem>
                  <SelectItem value="level_2">Level 2 - Full</SelectItem>
                  <SelectItem value="level_3">Level 3 - Enhanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Admin Notes</label>
              <Textarea
                value={editData.notes}
                onChange={(e) => setEditData({...editData, notes: e.target.value})}
                placeholder="Add notes about this update..."
                rows={3}
                data-testid="edit-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleEdit}
              disabled={actionLoading}
              data-testid="confirm-edit-btn"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main KYC Page Component
export default function KYC() {
  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [countries, setCountries] = useState([]);
  const [activeTab, setActiveTab] = useState('documents');
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [aml, setAml] = useState(null);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const params = {
        skip: page * limit,
        limit,
        status: statusFilter || undefined,
      };
      const response = await kycAPI.getAll(params);
      setDocuments(response.data.documents);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch KYC documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await countryAPI.getAll();
      setCountries(response.data);
    } catch (error) {
      console.error('Failed to fetch countries');
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchCountries();
    complianceAPI.aml().then((res) => setAml(res.data)).catch(() => {});
  }, [page, statusFilter]);

  const handleViewDetails = (docId) => {
    setSelectedDocId(docId);
  };

  const handleBackToList = () => {
    setSelectedDocId(null);
    fetchDocuments();
  };

  const totalPages = Math.ceil(total / limit);

  // If viewing document details, show detail view
  if (selectedDocId) {
    return (
      <div data-testid="kyc-page">
        <KYCDetailView 
          docId={selectedDocId} 
          onBack={handleBackToList}
          onActionComplete={handleBackToList}
        />
      </div>
    );
  }

  return (
    <div data-testid="kyc-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">KYC & Compliance</h1>
        <p className="page-subtitle">Verify user documents and manage compliance</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="documents" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <FileCheck className="w-4 h-4 mr-2" />
            KYC Documents
          </TabsTrigger>
          <TabsTrigger value="geo" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Globe className="w-4 h-4 mr-2" />
            Geo Compliance
          </TabsTrigger>
          <TabsTrigger value="aml" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <AlertTriangle className="w-4 h-4 mr-2" />
            AML Monitoring
          </TabsTrigger>
        </TabsList>

        {/* KYC Documents Tab */}
        <TabsContent value="documents">
          <div className="data-table-container">
            <div className="data-table-header">
              <h3 className="data-table-title">KYC Verification Queue</h3>
              <div className="filter-bar">
                <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val === 'all' ? '' : val); setPage(0); }}>
                  <SelectTrigger className="w-40" data-testid="kyc-status-filter">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="resubmission_required">Resubmission</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>User</TableHead>
                  <TableHead>Documents</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                    </TableCell>
                  </TableRow>
                ) : documents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                      No documents found
                    </TableCell>
                  </TableRow>
                ) : (
                  documents.map((doc) => (
                    <TableRow key={doc.id} className="table-row cursor-pointer hover:bg-muted/50" data-testid={`kyc-row-${doc.id}`}>
                      <TableCell>
                        <p className="font-medium">{doc.user_name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{doc.user_phone || doc.user_id}</p>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {doc.user_country === 'IN' || doc.document_type === 'pan_aadhaar' ? (
                            <>
                              <DocumentTypeBadge type="aadhaar" />
                              <DocumentTypeBadge type="pan" />
                            </>
                          ) : (
                            <DocumentTypeBadge type={doc.document_type} />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={doc.user_country === 'IN' ? 'text-orange-400 font-medium' : ''}>
                          {doc.user_country || '-'}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">{formatDateTime(doc.created_at)}</TableCell>
                      <TableCell>
                        <StatusBadge status={doc.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDetails(doc.id)}
                          data-testid={`view-kyc-${doc.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages - 1}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Geo Compliance Tab */}
        <TabsContent value="geo">
          <div className="data-table-container">
            <div className="data-table-header">
              <h3 className="data-table-title">Country Compliance Settings</h3>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Country</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Required Documents</TableHead>
                  <TableHead>Min Age</TableHead>
                  <TableHead>GST %</TableHead>
                  <TableHead>TDS %</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {countries.map((country) => (
                  <TableRow key={country.country_code} className="table-row">
                    <TableCell className="font-medium">{country.country_name}</TableCell>
                    <TableCell>{country.currency}</TableCell>
                    <TableCell>
                      {country.country_code === 'IN' ? (
                        <div className="flex gap-1">
                          <DocumentTypeBadge type="aadhaar" />
                          <DocumentTypeBadge type="pan" />
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          <DocumentTypeBadge type="passport" />
                          <span className="text-muted-foreground text-sm">or</span>
                          <DocumentTypeBadge type="driving_license" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{country.min_age}+</TableCell>
                    <TableCell className="font-mono">{country.gst_percentage}%</TableCell>
                    <TableCell className="font-mono">{country.tds_percentage}%</TableCell>
                    <TableCell>
                      <StatusBadge status={country.is_enabled ? 'active' : 'blocked'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* AML Monitoring Tab */}
        <TabsContent value="aml">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="stat-card">
              <AlertTriangle className="w-5 h-5 text-amber-400 mb-3" />
              <p className="stat-value">{aml?.large_transactions || 0}</p>
              <p className="stat-label">Deposits of ₹10,000+</p>
            </div>
            <div className="stat-card">
              <AlertTriangle className="w-5 h-5 text-red-400 mb-3" />
              <p className="stat-value">{aml?.withdrawals_without_kyc || 0}</p>
              <p className="stat-label">Withdrawals without KYC</p>
            </div>
            <div className="stat-card">
              <AlertTriangle className="w-5 h-5 text-orange-400 mb-3" />
              <p className="stat-value">{aml?.duplicate_phones || 0}</p>
              <p className="stat-label">Duplicate phone numbers</p>
            </div>
            <div className="stat-card">
              <AlertTriangle className="w-5 h-5 text-violet-400 mb-3" />
              <p className="stat-value">{aml?.banned_players || 0}</p>
              <p className="stat-label">Banned players</p>
            </div>
            <div className="stat-card">
              <AlertTriangle className="w-5 h-5 text-blue-400 mb-3" />
              <p className="stat-value">{aml?.pending_kyc || 0}</p>
              <p className="stat-label">KYC waiting for review</p>
            </div>
          </div>
          <div className="data-table-container mt-6">
            <div className="data-table-header">
              <h3 className="data-table-title">Live flags</h3>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Flag</TableHead>
                  <TableHead>Player</TableHead>
                  <TableHead>Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(aml?.rows || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-12 text-muted-foreground">No live flags</TableCell>
                  </TableRow>
                ) : (aml.rows || []).map((row, idx) => (
                  <TableRow key={`${row.kind}-${row.phone}-${idx}`} className="table-row">
                    <TableCell>{row.kind}</TableCell>
                    <TableCell>
                      <p className="font-medium">{row.user_name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{row.phone}</p>
                    </TableCell>
                    <TableCell>{row.detail}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
